import { useEffect, useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import vert from "./shaders/player.vert.glsl";
import frag from "./shaders/drone.frag.glsl";
import { audioBus } from "./audioBus";
import { DEFAULT_FX, type AudioFX } from "./audioFx";
import { fibDir, valueNoise3 } from "./visMath";
import type { VisStyle } from "./source";

const TAU = Math.PI * 2;
const TUN_RINGS = 64; // tunnel radius-history depth

/**
 * The 3D audio visualizer — the drones ARE the analyser. Positions are driven
 * live from `audioBus` every frame (frequency bins or time-domain samples),
 * eased so the cloud flows instead of strobing. Three styles:
 *
 *  - "corona"  — a radial spectrum sun: angle = frequency (log-ish), radius =
 *    magnitude. Slowly rotates; the low end blooms the whole disc.
 *  - "ribbon"  — the live waveform wrapped around a ring: each drone rides a
 *    sample of the actual PCM wave, radially and vertically.
 *  - "terrain" — a scrolling spectrogram landscape: x = frequency, z = time
 *    (history scrolls away from you), height + heat colour = magnitude.
 *  - "orb"     — an energy planet: drones sit on a Fibonacci sphere and the
 *    spectrum pushes the surface outward, pole (lows) to pole (highs).
 *  - "nebula"  — a value-noise cloud; bass drives the displacement amplitude,
 *    so the whole nebula breathes and churns with the low end.
 *  - "tunnel"  — rings flowing toward (and past) the camera; each ring's
 *    radius is the bass at the moment it spawned, so the low end literally
 *    shapes the tunnel you fly through.
 *
 * ADDING A STYLE: extend `VisStyle` + `VIS_STYLE_LIST` in source.ts, then add
 * a layout branch below (write `tgt`, optionally `col`). The ease + FX rack +
 * rest-relaxation come for free.
 *
 * Silence (or no audio connected) relaxes every style back to its rest shape.
 * FPV can fly through all of them.
 */
export function VisualizerField({
  count,
  style,
  brightness = 1.5,
  size = 6.0,
  fx = DEFAULT_FX,
}: {
  count: number;
  style: VisStyle;
  brightness?: number;
  size?: number;
  fx?: AudioFX;
}) {
  const matRef = useRef<THREE.ShaderMaterial>(null);
  const mountN = useRef(count);
  const N = mountN.current;
  const styleRef = useRef(style);
  styleRef.current = style;
  const fxRef = useRef(fx);
  fxRef.current = fx;
  const hueAngle = useRef(0);

  const sim = useMemo(() => {
    const side = Math.max(2, Math.ceil(Math.sqrt(N)));
    const seeds = new Float32Array(N);
    const dirs = new Float32Array(N * 3); // orb: Fibonacci-sphere unit dirs
    const anch = new Float32Array(N * 3); // nebula: rest anchors (uniform ball)
    for (let i = 0; i < N; i++) {
      seeds[i] = Math.random();
      const d = fibDir(i, N);
      dirs[i * 3] = d[0]; dirs[i * 3 + 1] = d[1]; dirs[i * 3 + 2] = d[2];
      const rr = 3.1 * Math.cbrt(Math.random()); // uniform in a ball
      const th = Math.random() * TAU;
      const cph = 2 * Math.random() - 1;
      const sph = Math.sqrt(Math.max(0, 1 - cph * cph));
      anch[i * 3] = rr * sph * Math.cos(th);
      anch[i * 3 + 1] = rr * cph;
      anch[i * 3 + 2] = rr * sph * Math.sin(th);
    }
    return {
      pos: new Float32Array(N * 3),
      tgt: new Float32Array(N * 3),
      col: new Float32Array(N * 3),
      seeds,
      dirs,
      anch,
      side, // terrain grid side (cols × rows)
      hist: new Float32Array(side * side), // terrain magnitude history (rows ring)
      rowPtr: 0,
      hopT: 0, // time until the terrain scrolls one row
      tunHist: new Float32Array(TUN_RINGS).fill(2.0), // tunnel ring radii (bass history)
      tunPtr: 0,
      tunHop: 0,
      tunPhase: 0, // tunnel travel phase (0..1 = one full ring cycle)
      init: false,
      lastStyle: "" as string,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const geometry = useMemo(() => {
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(sim.pos, 3));
    g.setAttribute("aColor", new THREE.BufferAttribute(sim.col, 3));
    g.setAttribute("aSeed", new THREE.BufferAttribute(sim.seeds, 1));
    return g;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sim]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uSize: { value: size },
      uBrightness: { value: brightness },
      uAudioLevel: { value: 0 },
      uAudioBeat: { value: 0 },
      uShimmer: { value: 0 },
      uHueShift: { value: 0 },
      uCymAmp: { value: 0 },
      uCymM: { value: 0 },
      uCymN: { value: 0 },
      uCymMix: { value: -1 },
      uCymL: { value: 1 },
      uCymSpeed: { value: 0 },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );
  useEffect(() => {
    if (!matRef.current) return;
    matRef.current.uniforms.uSize.value = size;
    matRef.current.uniforms.uBrightness.value = brightness;
  }, [size, brightness]);

  // sample a frequency array with a gentle log bias (music lives in the lows)
  const freqAt = (freq: Uint8Array | null, u: number, maxHzFrac: number) => {
    if (!freq) return 0;
    const b = 1 + Math.floor(Math.pow(u, 1.6) * (freq.length * maxHzFrac - 1));
    return freq[b] / 255;
  };

  useFrame((state, dtRaw) => {
    const m = matRef.current;
    if (!m) return;
    const dt = Math.min(dtRaw, 1 / 30);
    const time = state.clock.elapsedTime;
    m.uniforms.uTime.value = time;

    // the shader-side FX rack (Pulse / Shimmer / Hue), same as the fields
    const av = audioBus.values;
    const fxc = fxRef.current;
    m.uniforms.uAudioLevel.value = fxc.pulse.on ? av.level * fxc.pulse.amt : 0;
    m.uniforms.uAudioBeat.value = fxc.pulse.on ? av.beat * fxc.pulse.amt : 0;
    m.uniforms.uShimmer.value = fxc.shimmer.on ? av.treble * fxc.shimmer.amt : 0;
    if (fxc.hue.on) hueAngle.current += av.mid * fxc.hue.amt * dt * 0.6;
    m.uniforms.uHueShift.value = fxc.hue.on ? hueAngle.current : 0;

    const st = styleRef.current;
    const { pos, tgt, col, seeds, side, hist, dirs, anch, tunHist } = sim;
    const freq = audioBus.freq;
    const wave = audioBus.wave;
    const hc = new THREE.Color();
    const styleChanged = sim.lastStyle !== st;
    sim.lastStyle = st;

    if (st === "corona") {
      // ---- radial spectrum sun ----
      for (let i = 0; i < N; i++) {
        const u = i / N;
        const mag = freqAt(freq, u, 0.35); // up to ~8 kHz
        const theta = u * TAU + time * 0.12;
        const r = 2.4 + seeds[i] * 0.3 + mag * 3.2 + av.bass * 0.5;
        tgt[i * 3] = Math.cos(theta) * r;
        tgt[i * 3 + 1] = Math.sin(theta) * r;
        tgt[i * 3 + 2] = (seeds[i] - 0.5) * (0.5 + mag * 1.4);
        if (styleChanged) {
          hc.setHSL(0.02 + u * 0.9, 0.9, 0.55);
          col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
        }
      }
    } else if (st === "ribbon") {
      // ---- the live waveform around a ring ----
      const L = wave ? wave.length : 0;
      for (let i = 0; i < N; i++) {
        const u = i / N;
        const s = L ? (wave![Math.floor(u * (L - 1))] - 128) / 128 : 0; // −1..1
        const phi = u * TAU + time * 0.06;
        const r = 3.1 + s * 1.3;
        tgt[i * 3] = Math.cos(phi) * r;
        tgt[i * 3 + 1] = s * 1.5 + Math.sin(phi * 2 + time * 0.4) * 0.12;
        tgt[i * 3 + 2] = Math.sin(phi) * r;
        if (styleChanged) {
          hc.setHSL(0.5 + u * 0.5, 0.85, 0.6);
          col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
        }
      }
    } else if (st === "orb") {
      // ---- energy planet: the spectrum pushes the sphere's surface out ----
      const ca = Math.cos(time * 0.1), sa = Math.sin(time * 0.1);
      for (let i = 0; i < N; i++) {
        const u = i / N; // fib index descends pole→pole = a latitude/frequency axis
        const mag = freqAt(freq, u, 0.35);
        const r = 2.3 + mag * 2.1 + av.bass * 0.35;
        const dx = dirs[i * 3], dy = dirs[i * 3 + 1], dz = dirs[i * 3 + 2];
        tgt[i * 3] = (dx * ca + dz * sa) * r; // slow spin about Y
        tgt[i * 3 + 1] = dy * r;
        tgt[i * 3 + 2] = (-dx * sa + dz * ca) * r;
        // living surface: heat by magnitude
        hc.setHSL(0.62 - mag * 0.5, 0.85, 0.3 + mag * 0.45);
        col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
      }
    } else if (st === "nebula") {
      // ---- a noise cloud breathing with the bass ----
      const t = time * 0.22;
      const amp = 0.55 + av.bass * 2.4 + av.level * 0.6;
      const breathe = 1 + av.bass * 0.22;
      const NS = 0.5; // noise spatial scale
      for (let i = 0; i < N; i++) {
        const bx = anch[i * 3], by = anch[i * 3 + 1], bz = anch[i * 3 + 2];
        const n1 = valueNoise3(bx * NS + t, by * NS, bz * NS);
        const n2 = valueNoise3(bx * NS + 31.7, by * NS + t, bz * NS + 11.3);
        const n3 = valueNoise3(bx * NS + 71.1, by * NS + 47.9, bz * NS + t);
        tgt[i * 3] = (bx + n1 * amp) * breathe;
        tgt[i * 3 + 1] = (by + n2 * amp) * breathe;
        tgt[i * 3 + 2] = (bz + n3 * amp) * breathe;
        // nebula palette: purple→pink→cyan wandering on the noise
        hc.setHSL(0.72 + n1 * 0.14, 0.8, 0.42 + Math.abs(n2) * 0.25);
        col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
      }
    } else if (st === "tunnel") {
      // ---- bass tunnel: rings shaped by the low end flow past you ----
      sim.tunHop -= dt;
      if (sim.tunHop <= 0) {
        sim.tunHop = 0.07; // a new ring every 70 ms → ~4.5 s of bass memory
        sim.tunPtr = (sim.tunPtr + 1) % TUN_RINGS;
        tunHist[sim.tunPtr] = 2.0 + av.bass * 2.6 + av.beat * 0.5;
      }
      sim.tunPhase = (sim.tunPhase + dt * (0.06 + av.level * 0.05)) % 1;
      const perRing = Math.max(1, Math.ceil(N / TUN_RINGS));
      const Z_NEAR = 8, Z_FAR = -26; // camera at z=12: rings spawn just ahead, recede
      // (newest bass = the ring at your face → instant audio sync; the history
      // recedes down the bore like a waterfall in tunnel form)
      for (let i = 0; i < N; i++) {
        const ring = Math.floor(i / perRing);
        const k = i % perRing;
        const zFrac = (ring / TUN_RINGS + sim.tunPhase) % 1; // 0 = at the camera
        const row = (sim.tunPtr - Math.floor(zFrac * TUN_RINGS) + TUN_RINGS * 2) % TUN_RINGS;
        const phi = (k / perRing) * TAU + ring * 0.31 + time * 0.05;
        const r = tunHist[row] + Math.sin(phi * 3 + time * 2.1) * 0.07;
        tgt[i * 3] = Math.cos(phi) * r;
        tgt[i * 3 + 1] = Math.sin(phi) * r;
        tgt[i * 3 + 2] = Z_NEAR + (Z_FAR - Z_NEAR) * zFrac;
        // hue cycles down the bore; distant rings dim
        hc.setHSL((0.55 + zFrac * 0.5) % 1, 0.85, 0.55 - zFrac * 0.3);
        col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
      }
    } else {
      // ---- scrolling spectrogram terrain ----
      sim.hopT -= dt;
      if (sim.hopT <= 0) {
        sim.hopT = 0.05; // one history row every 50 ms (~ side/20 s of memory)
        sim.rowPtr = (sim.rowPtr + 1) % side;
        const base = sim.rowPtr * side;
        for (let cIx = 0; cIx < side; cIx++) hist[base + cIx] = freqAt(freq, cIx / (side - 1), 0.3);
      }
      const EX = 8.5, EZ = 8.5;
      for (let i = 0; i < N; i++) {
        const cIx = i % side;
        const rIx = Math.floor(i / side); // 0 = the newest row
        const row = (sim.rowPtr - rIx + side * 2) % side;
        const h = hist[row * side + cIx];
        tgt[i * 3] = (cIx / (side - 1) - 0.5) * EX;
        tgt[i * 3 + 1] = -1.2 + h * 3.0;
        tgt[i * 3 + 2] = (0.5 - rIx / (side - 1)) * EZ; // newest at the front
        // heat colour by height: deep blue → cyan → white-hot
        hc.setHSL(0.62 - h * 0.45, 0.9, 0.25 + h * 0.55);
        col[i * 3] = hc.r; col[i * 3 + 1] = hc.g; col[i * 3 + 2] = hc.b;
      }
    }

    // first frame (or a style hop): snap into place instead of flying from 0
    if (!sim.init || styleChanged) {
      pos.set(tgt);
      sim.init = true;
    } else {
      const k = Math.min(1, dt * 10); // fast ease — live but not strobing
      for (let i = 0; i < N; i++) {
        const j = i * 3;
        const dz = tgt[j + 2] - pos[j + 2];
        if (dz > 14 || dz < -14) {
          // a tunnel ring recycling front→back: teleport, don't streak the bore
          pos[j] = tgt[j]; pos[j + 1] = tgt[j + 1]; pos[j + 2] = tgt[j + 2];
        } else {
          pos[j] += (tgt[j] - pos[j]) * k;
          pos[j + 1] += (tgt[j + 1] - pos[j + 1]) * k;
          pos[j + 2] += (tgt[j + 2] - pos[j + 2]) * k;
        }
      }
    }

    (geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    (geometry.getAttribute("aColor") as THREE.BufferAttribute).needsUpdate = true;
  });

  return (
    <points name="swarm" geometry={geometry} frustumCulled={false}>
      <shaderMaterial
        ref={matRef}
        vertexShader={vert}
        fragmentShader={frag}
        uniforms={uniforms}
        transparent
        depthWrite={false}
        depthTest={false}
        blending={THREE.AdditiveBlending}
      />
    </points>
  );
}
